import React, { useState, useEffect } from 'react';
import { TextArea, Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, InputNumber } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import { useLocation } from 'react-router-dom';
import request from '../../../utils/request';
import styles from './index.module.scss';
import ImageSelector from '../../../components/ImageSelector';

const Qrcode = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const groupUid = searchParams.get('group_uid');

    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);

    const statusMap = {
        0: { text: '启用', type: 'success' },
        1: { text: '禁用', type: 'danger' },
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/join_group/list_jg_qrcode/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    group_uid: groupUid
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (groupUid) {
            fetchList();
        }
    }, [groupUid]);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/join_group/add_jg_qrcode/', {
                ...values,
                group_uid: groupUid,
                qr_cfg: JSON.stringify({
                    url: values.qr_url,
                    desc: values.qr_desc
                })
            });
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/join_group/edit_jg_qrcode/', {
                ...values,
                uid: currentRecord.uid,
                group_uid: groupUid,
                view_cnt: formData.view_cnt,
                qr_cfg: JSON.stringify({
                    url: values.qr_url,
                    desc: values.qr_desc
                })
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/join_group/delete_jg_qrcode/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的二维码');
            return;
        }
        try {
            const response = await request.post('/go/sp/join_group/batchedit_jg_qrcode/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '二维码信息',
            dataIndex: 'qr_cfg',
            width: 300,
            render: (text, record) => {
                const qrCfg = typeof text === 'string' ? JSON.parse(text) : text;
                return (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        {qrCfg?.url && <img src={qrCfg.url} alt={record.name} style={{ width: 80, height: 80, objectFit: 'contain' }} />}
                        <div style={{ fontSize: '12px' }}>                            <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{record.name || '-'}</div>                            <div style={{ color: 'var(--semi-color-text-2)' }}>{qrCfg?.desc || '-'}</div>                            <div style={{ marginTop: '4px' }}>{record.group?.name || '-'}</div> </div>
                    </div>
                );
            }
        },
        {
            title: '展示次数',
            dataIndex: 'view_cnt',
            width: 200,
            render: (text, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div>已展示：{text || 0}</div>
                    <div>最大展示：{record.max_cnt || 100}</div>
                </div>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '排序',
            dataIndex: 'sort',
            width: 100
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            const qrCfg = typeof record.qr_cfg === 'string' ? JSON.parse(record.qr_cfg) : record.qr_cfg;
                            setCurrentRecord({
                                ...record,
                                qr_url: qrCfg?.url,
                                qr_desc: qrCfg?.desc
                            });
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该二维码吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const initialFormData = {
        name: '',
        qr_url: '',
        qr_desc: '',
        max_cnt: 100,
        sort: 0,
        status: 0,
        view_cnt: 0
    };

    const [formData, setFormData] = useState(initialFormData);

    const [groupInfo, setGroupInfo] = useState(null);

    const fetchGroupInfo = async () => {
        if (!groupUid) return;
        try {
            const response = await request.get('/go/sp/join_group/get_jg_group/', {
                params: { uid: groupUid }
            });
            if (response.errno === 0 && response.data) {
                setGroupInfo(response.data);
            }
        } catch (error) {
            console.error('获取群组信息失败:', error);
        }
    };

    useEffect(() => {
        fetchGroupInfo();
    }, [groupUid]);


    useEffect(() => {
        if (currentRecord) {
            const qrCfg = typeof currentRecord.qr_cfg === 'string' ? JSON.parse(currentRecord.qr_cfg) : currentRecord.qr_cfg;
            setFormData({
                name: currentRecord.name,
                qr_url: qrCfg?.url,
                qr_desc: qrCfg?.desc,
                view_cnt: currentRecord.view_cnt || 0,
                max_cnt: currentRecord.max_cnt || 100,
                sort: currentRecord.sort || 0,
                status: currentRecord.status || 0
            });
        }
    }, [currentRecord]);

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';

        const handleSubmit = () => {
            if (!formData.qr_url) {
                Toast.error('请上传二维码图片');
                return;
            }
            if (isEdit) {
                handleEdit(formData);
            } else {
                handleAdd(formData);
            }
        };

        return (
            <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>备注（后台管理员可见）</div>
                    <Input
                        value={formData.name}
                        onChange={value => setFormData(prev => ({ ...prev, name: value }))}
                        placeholder="请输入备注，仅后台管理员可见"
                    />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>二维码图片 <span style={{ color: 'var(--semi-color-danger)' }}>*</span></div>
                    <ImageSelector
                        value={formData.qr_url}
                        onChange={url => setFormData(prev => ({ ...prev, qr_url: url }))}
                        placeholder="请上传二维码图片"
                        width="100%"
                        previewSize={100}
                    />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>图片备注（用户可见）</div>
                    <TextArea
                        value={formData.qr_desc}
                        onChange={value => setFormData(prev => ({ ...prev, qr_desc: value }))}
                        placeholder="请输入图片备注，该备注将展示给用户查看"
                    />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>展示次数</div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <div style={{ color: 'var(--semi-color-text-2)' }}>已展示：</div>
                        <InputNumber
                            value={formData.view_cnt}
                            onChange={value => setFormData(prev => ({ ...prev, view_cnt: value }))}
                            placeholder="请输入已展示次数"
                            min={0}
                            style={{ width: '120px' }}
                        />
                        <div style={{ color: 'var(--semi-color-text-2)' }}>最大展示：</div>
                        <InputNumber
                            value={formData.max_cnt}
                            onChange={value => setFormData(prev => ({ ...prev, max_cnt: value }))}
                            placeholder="请输入最大展示次数"
                            min={1}
                            style={{ width: '120px' }}
                        />
                    </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>排序</div>
                    <InputNumber
                        value={formData.sort}
                        onChange={value => setFormData(prev => ({ ...prev, sort: value }))}
                        placeholder="请输入排序值（大的在前面）"
                        min={0}
                    />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>状态</div>
                    <Select
                        value={formData.status}
                        onChange={value => setFormData(prev => ({ ...prev, status: value }))}
                        style={{ width: '100%' }}
                    >
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                </div>
                <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' onClick={handleSubmit}>确定</Button>
                    </Space>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.qrcodeContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索备注"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => {
                        setFormData(initialFormData);
                        setShowAddModal(true);
                    }}>新增二维码</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量禁用</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量启用</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个二维码吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.currentPage,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title={`新增二维码 - ${groupInfo?.title}`}
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title={`编辑二维码 - ${groupInfo?.title}`}
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
            >
                {renderForm('edit')}
            </Modal>
        </div>
    );
};

export default Qrcode;